import moment from "moment";
import { Component, Inject, Prop } from "vue-property-decorator";
import { AbsencesPlanningMensuelDto } from "../../../../dto/GenApi.iNotPaie.Dto/AbsencesPlanningMensuelDto";
import { ActionAbsencesEnum } from "../../../../dto/GenApi.iNotPaie.Dto/ActionAbsencesEnum";
import { CommandeChangementEtatAbsenceDto } from "../../../../dto/GenApi.iNotPaie.Dto/CommandeChangementEtatAbsenceDto";
import { getAbsenceActionsState } from "../../../../Helper/absenceActionsHelper";
import {
    buildAbsenceClassesMensuel,
    cleanPopperElm,
    cleanTooltipPopper, continuesLeft as continuesLeftHelper,
    continuesRight as continuesRightHelper, convertAbsenceToSidePanelHistorique,
    executeChangementEtatAbsence, getAbsenceStyle,
    getBorderRadiusClass,
    getCancelAbsenceModalOptions,
    getCancelCancellationModalOptions,
    getDayIndexInWeek, getDureeAbsence, getHalfDayFlags, getLibelleAbsence, getStateInfo,
    getTypeIcon,
    isValidatedPlanning
} from "../../../../Helper/absencePlanningHelper";
import { CalendarDay, IAbsenceStyle } from "../../../../shared/interfaces/calendar.interface";
import { ISidePanelHistorique } from "../../../../shared/interfaces/sidePanelHistorique.interface";
import { NotificationServiceInstance } from "../../../../shared/services/notification.service";
import { EventBus } from "../../../Controls/EventBus";
import EventConstants from "../../../Controls/EventConstants";
import { VueBase } from "../../../Controls/VueBase";

/**
 * Composant d'une barre d'absence dans le calendrier mensuel (vue par semaine).
 * Une absence peut s'étendre sur plusieurs semaines : il y a une instance par semaine affichée.
 * Gère le positionnement (day-X, duration-Y), les demi-journées, les bords tronqués, le menu et l'historique.
 */
@Component({
    components: {
        SidePanelHistorique: require("../../sidePanelHistorique/sidePanelHistorique.vue.html")
    }
})
export default class AbsencePlanningMensuel extends VueBase {
    @Prop({ required: true }) public absence!: AbsencesPlanningMensuelDto;
    @Prop({ required: true }) public weekDays!: CalendarDay[];
    @Prop({ required: true }) public weekNumber!: number;
    /** Map absenceId -> numéro de la première semaine où l'absence apparaît (pour n'afficher le détail que sur cette semaine). */
    @Prop({ required: true }) public firstWeekMap!: Map<number, number>;

    @Inject("modifyAbsence") readonly modifyAbsence!: (id: number) => void;

    public drawerAbsence: ISidePanelHistorique | null = null;
    public drawerIsVisible = false;
    public menuPopoverVisible = false;

    /** L'absence débute avant le lundi de cette semaine (barre tronquée à gauche). */
    private continuesLeft(): boolean {
        return continuesLeftHelper(this.absence.DateDebut, this.weekDays[0].date);
    }

    /** L'absence se termine après le dimanche de cette semaine (barre tronquée à droite). */
    private continuesRight(): boolean {
        return continuesRightHelper(this.absence.DateFin, this.weekDays[6].date);
    }

    /** Données d'affichage pour le template : style visuel, icône du type d'absence, libellé/icône d'état. */
    public get displayInfo(): { style: IAbsenceStyle; typeIcon: string; stateInfo: { title: string; icon: string } } {
        return {
            style: getAbsenceStyle(this.absence.Color as string, isValidatedPlanning(this.absence.Etat)),
            typeIcon: getTypeIcon(this.absence.CodeTypeAbsence),
            stateInfo: getStateInfo(this.absence.Etat)
        };
    }

    /** True si on doit afficher le détail (libellé, dates, menu) : uniquement sur la première semaine où l'absence apparaît. */
    public get showText(): boolean {
        return this.firstWeekMap.get(this.absence.Id) === this.weekNumber;
    }

    /**
     * Liste de classes CSS pour positionner et styliser la barre d'absence dans la grille de la semaine.
     * Utilisé par le template sur l'élément de la barre.
     * Contient notamment :
     * - day-X / duration-Y : position et largeur dans la grille 7 colonnes (lundi=1 … dimanche=7)
     * - half-day-start / half-day-end : si l'absence ne couvre qu'une demi-journée au bord
     * - continues-left / continues-right : bords tronqués (absence déborde de la semaine)
     * - single | start-only | end-only | middle : arrondi des bords
     * - absence-group-{id} : pour grouper les segments d'une même absence (hover, click)
     */
    public get absenceClasses(): string[] {
        const absenceTruncatedOnLeft = this.continuesLeft();
        const absenceTruncatedOnRight = this.continuesRight();

        // Premier et dernier jour de l'absence visibles dans cette semaine (1 = lundi, 7 = dimanche)
        const firstDisplayedDayInWeek = absenceTruncatedOnLeft ? 1 : getDayIndexInWeek(this.absence.DateDebut, this.weekDays, 1);
        const lastDisplayedDayInWeek = absenceTruncatedOnRight ? 7 : getDayIndexInWeek(this.absence.DateFin, this.weekDays, 7);
        const displayedDayCount = lastDisplayedDayInWeek - firstDisplayedDayInWeek + 1;

        const isSameDay = moment(this.absence.DateDebut).isSame(moment(this.absence.DateFin), "day");
        const { halfDayStart, halfDayEnd } = getHalfDayFlags(
            this.absence.DebutMatinAprem,
            this.absence.FinMatinAprem,
            isSameDay,
            absenceTruncatedOnLeft,
            absenceTruncatedOnRight
        );

        const barBorderRadiusClass = getBorderRadiusClass(absenceTruncatedOnLeft, absenceTruncatedOnRight);

        return buildAbsenceClassesMensuel(
            firstDisplayedDayInWeek,
            displayedDayCount,
            halfDayStart,
            halfDayEnd,
            absenceTruncatedOnLeft,
            absenceTruncatedOnRight,
            barBorderRadiusClass,
            this.absence.Id
        );
    }

    /** Durée formatée (ex. "3j") pour l'affichage. */
    public getDuration(): string {
        return getDureeAbsence(this.absence.NombreJours, this.absence.NombreHeures);
    }

    public onTooltipHide(): void {
        cleanTooltipPopper(this.$refs.tooltip as any);
    }

    public onPopOverHide(): void {
        cleanPopperElm(this.$refs.menuPopover as any);
    }

    /**
     * Gestionnaire de survol d'une absence
     * Applique l'effet hover à toutes les parties de la même absence (multi-semaines)
     * @param absence Absence survolée
     */
    public onAbsenceMouseEnter(absence: AbsencesPlanningMensuelDto): void {
        const groupElements = document.querySelectorAll(`.absence-group-${absence.Id}`);
        groupElements.forEach((element: Element) => {
            element.classList.add('hover-group-absence');
        });
    }

    /**
     * Gestionnaire de sortie de survol d'une absence
     * Retire l'effet hover de toutes les parties de la même absence
     * @param absence Absence dont on sort
     */
    public onAbsenceMouseLeave(absence: AbsencesPlanningMensuelDto): void {
        this.onTooltipHide();

        const groupElements = document.querySelectorAll(`.absence-group-${absence.Id}`);
        groupElements.forEach((element: Element) => {
            element.classList.remove('hover-group-absence');
        });
    }

    /**
     * Gestionnaire de click d'une absence
     * Ouvre le menu pop-over sur la première apparition de l'absence et cache le tooltip.
     * Si le click est sur une semaine secondaire, on délègue à la première apparition.
     */
    public onAbsenceClicked(absence: AbsencesPlanningMensuelDto, event?: MouseEvent) {
        // Si ce n'est pas la première semaine de l'absence, déléguer le click à la première apparition
        if (this.firstWeekMap.get(this.absence.Id) !== this.weekNumber) {
            if (event) {
                event.stopPropagation();
            }
            this.onTooltipHide();
            const firstElement = document.querySelector(`.absence-group-${absence.Id}`) as HTMLElement;
            if (firstElement) {
                firstElement.click();
            }
            return;
        }

        this.menuPopoverVisible = !this.menuPopoverVisible;

        if (this.menuPopoverVisible) {
            this.onTooltipHide();
        }
        else {
            this.onPopOverHide();
        }
    }

    /**
     * Gestionnaire de click externe d'une absence
     * Ferme le menu pop-over.
     */
    public onAbsenceClickedOutside() {
        if (this.menuPopoverVisible) {
            this.menuPopoverVisible = false;
            this.onPopOverHide();
        }
    }

    /**
    * Ouvre le drawer pour afficher l'historique d'une absence spécifique.
    * @param absence L'absence pour laquelle afficher l'historique.
    */
    public openDrawerHistorique(absence: AbsencesPlanningMensuelDto): void {
        this.menuPopoverVisible = false;
        this.$nextTick(() => {
            this.drawerAbsence = this.convertAbsenceToDisplay(absence);
            this.drawerIsVisible = true;
        });
    }

    public get actionsState() {
        return getAbsenceActionsState({
            etat: this.absence.Etat!,
            poseeParGestionnaire: this.absence.PoseeParGestionnaire,
        });
    }

    /**
    * Émet un événement pour modifier l'absence sélectionnée.
    */
    public modify(): void {
        this.menuPopoverVisible = false;
        this.modifyAbsence(this.absence.Id);
    }

    public cancel(): void {
        this.menuPopoverVisible = false;
        EventBus.$emit(
            EventConstants.MODAL_ALERT,
            getCancelAbsenceModalOptions(() =>
                this.changeEtatAbsence(
                    ActionAbsencesEnum.DemanderAnnulerAbsenceParSalarie,
                    "L'annulation de l'absence n'a pas pu être effectuée."
                )
            )
        );
    }

    public cancelCancellation(): void {
        this.menuPopoverVisible = false;
        EventBus.$emit(
            EventConstants.MODAL_ALERT,
            getCancelCancellationModalOptions(() =>
                this.changeEtatAbsence(
                    ActionAbsencesEnum.AnnulerDemandeAnnulationAbsenceParSalarie,
                    "La suppression de la demande d'annulation de l'absence n'a pas pu être effectuée."
                )
            )
        );
    }

    private changeEtatAbsence(action: ActionAbsencesEnum, messageErreur: string): void {
        const commande: CommandeChangementEtatAbsenceDto = {
            IdAbsence: this.absence.Id,
            Action: action,
            Etat: this.absence.Etat!
        };
        executeChangementEtatAbsence(
            commande,
            () => EventBus.$emit(EventConstants.UPDATE_CONGES),
            () => NotificationServiceInstance.SendFailed(messageErreur)
        );
    }

    private convertAbsenceToDisplay(absence: AbsencesPlanningMensuelDto): ISidePanelHistorique {
        return convertAbsenceToSidePanelHistorique(
            absence,
            getLibelleAbsence(absence.LibelleTypeAbsence, absence.PoseeParGestionnaire)
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/gestionDesAbsencesV2/calendrierMensuel/absencePlanningMensuel/absencePlanningMensuel.ts